import React, { useState, useEffect } from 'react';
import { 
  Accessibility, 
  X, 
  Type, 
  Contrast, 
  MousePointer2, 
  Link2, 
  Eye,
  Volume2,
  Pause,
  RotateCcw,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    highlightLinks: false,
    largeCursor: false,
    dyslexiaFont: false,
    pauseAnimations: false,
    focusMode: false,
  });

  // Apply settings to document
  const applySettings = React.useCallback((newSettings) => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${newSettings.fontSize}%`;
    
    // High contrast
    if (newSettings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Highlight links
    if (newSettings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
    
    // Large cursor
    if (newSettings.largeCursor) {
      root.classList.add('large-cursor');
    } else {
      root.classList.remove('large-cursor');
    }
    
    // Dyslexia font
    if (newSettings.dyslexiaFont) {
      root.classList.add('dyslexia-font');
    } else {
      root.classList.remove('dyslexia-font');
    }
    
    // Pause animations
    if (newSettings.pauseAnimations) {
      root.classList.add('pause-animations');
    } else {
      root.classList.remove('pause-animations');
    }
    
    // Focus mode
    if (newSettings.focusMode) {
      root.classList.add('focus-mode');
    } else {
      root.classList.remove('focus-mode');
    }
  }, []);

  // Load settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, [applySettings]);

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('accessibility-settings', JSON.stringify(newSettings));
    applySettings(newSettings);
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      highlightLinks: false,
      largeCursor: false,
      dyslexiaFont: false,
      pauseAnimations: false,
      focusMode: false,
    };
    setSettings(defaultSettings);
    localStorage.removeItem('accessibility-settings');
    applySettings(defaultSettings);
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSetting('fontSize', settings.fontSize + 10);
    }
  };

  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSetting('fontSize', settings.fontSize - 10);
    }
  };

  return (
    <>
      {/* Accessibility Styles */}
      <style>{`
        .high-contrast {
          filter: contrast(1.25) !important;
        }
        .high-contrast * {
          border-color: currentColor !important;
        }
        .highlight-links a {
          background-color: #ffff00 !important;
          color: #000000 !important;
          padding: 2px 4px !important;
          text-decoration: underline !important;
        }
        .large-cursor, .large-cursor * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='%23000' stroke='%23fff' stroke-width='1'%3E%3Cpath d='M4 4l16 8-8 2-2 8z'/%3E%3C/svg%3E") 0 0, auto !important;
        }
        .dyslexia-font * {
          font-family: 'OpenDyslexic', 'Comic Sans MS', cursive, sans-serif !important;
          letter-spacing: 0.05em !important;
          word-spacing: 0.1em !important;
          line-height: 1.8 !important;
        }
        .pause-animations *, .pause-animations *::before, .pause-animations *::after {
          animation: none !important;
          transition: none !important;
        }
        .focus-mode *:not(:focus):not(:hover) {
          opacity: 0.7;
        }
        .focus-mode *:focus, .focus-mode *:hover {
          opacity: 1;
          outline: 3px solid #a855f7 !important;
          outline-offset: 2px !important;
        }
      `}</style>

      {/* Floating Button - Right Side */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed right-4 bottom-4 z-50 p-3 rounded-full shadow-lg transition-all duration-300',
          'bg-primary text-primary-foreground hover:scale-110',
          'focus:outline-none focus:ring-4 focus:ring-primary/50',
          isOpen && 'rotate-180'
        )}
        aria-label="Accessibility Options"
        aria-expanded={isOpen}
        data-testid="accessibility-widget-btn"
      >
        <Accessibility className="w-6 h-6" />
      </button>

      {/* Widget Panel - Right Side */}
      {isOpen && (
        <div
          className="fixed right-4 bottom-20 z-50 w-80 max-h-[80vh] overflow-y-auto bg-card border rounded-2xl shadow-2xl"
          role="dialog"
          aria-label="Accessibility Settings"
        >
          {/* Header */}
          <div className="sticky top-0 bg-card border-b p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-primary" />
              <h2 className="font-bold">Accessibility</h2>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={resetSettings}
                title="Reset all settings"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Settings */}
          <div className="p-4 space-y-4">
            {/* Font Size */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Type className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Text Size</span>
                </div>
                <span className="text-sm font-mono">{settings.fontSize}%</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={decreaseFontSize}
                  disabled={settings.fontSize <= 80}
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <div className="flex-1 h-2 bg-background rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${((settings.fontSize - 80) / 70) * 100}%` }}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={increaseFontSize}
                  disabled={settings.fontSize >= 150}
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Toggle Options */}
            <div className="space-y-2">
              {/* High Contrast */}
              <ToggleOption
                icon={Contrast}
                label="High Contrast"
                description="Increase color contrast"
                checked={settings.highContrast}
                onChange={(v) => updateSetting('highContrast', v)}
              />

              {/* Highlight Links */}
              <ToggleOption
                icon={Link2}
                label="Highlight Links"
                description="Make links more visible"
                checked={settings.highlightLinks}
                onChange={(v) => updateSetting('highlightLinks', v)}
              />

              {/* Large Cursor */}
              <ToggleOption
                icon={MousePointer2}
                label="Large Cursor"
                description="Bigger mouse pointer"
                checked={settings.largeCursor}
                onChange={(v) => updateSetting('largeCursor', v)}
              />

              {/* Dyslexia Font */}
              <ToggleOption
                icon={Eye}
                label="Dyslexia Friendly"
                description="Easier to read font"
                checked={settings.dyslexiaFont}
                onChange={(v) => updateSetting('dyslexiaFont', v)}
              />

              {/* Pause Animations */}
              <ToggleOption
                icon={Pause}
                label="Stop Animations"
                description="Pause all motion"
                checked={settings.pauseAnimations}
                onChange={(v) => updateSetting('pauseAnimations', v)}
              />

              {/* Focus Mode */}
              <ToggleOption
                icon={Eye}
                label="Focus Mode"
                description="Highlight focused elements"
                checked={settings.focusMode}
                onChange={(v) => updateSetting('focusMode', v)}
              />
            </div>

            {/* WCAG Badge */}
            <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">AA</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">WCAG 2.1 AA</p>
                  <p className="text-xs text-muted-foreground">Accessibility Compliant</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Toggle Option Component
const ToggleOption = ({ icon: Icon, label, description, checked, onChange }) => (
  <div 
    className="flex items-center justify-between p-3 bg-muted/50 hover:bg-muted rounded-lg cursor-pointer transition-colors"
    onClick={() => onChange(!checked)}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === 'Enter' && onChange(!checked)}
  >
    <div className="flex items-center gap-3">
      <Icon className={cn("w-4 h-4", checked ? "text-primary" : "text-muted-foreground")} />
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
    <div 
      className={cn(
        "w-10 h-6 rounded-full transition-colors relative",
        checked ? "bg-primary" : "bg-muted-foreground/30"
      )}
    >
      <div 
        className={cn(
          "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform",
          checked ? "translate-x-5" : "translate-x-1"
        )}
      />
    </div>
  </div>
);

export default AccessibilityWidget;
